/**
 * Financial Knowledge Base
 * Comprehensive definitions and explanations for financial and economic concepts
 * 
 * NOTE: This file now serves as a compatibility layer.
 * All actual knowledge content is stored in translation files (en.json, es.json)
 * under the "financialKnowledge" key.
 */

import { getFinancialKnowledge as getTranslatedKnowledge, getInterpretation } from './financialKnowledgeTranslations';

// Legacy export for backward compatibility - now uses translations
export const FINANCIAL_EXPLANATIONS = {
    vix: {
        what: "The **VIX (CBOE Volatility Index)** measures market expectations for 30-day volatility based on S&P 500 option prices. It's often called the 'fear gauge' because it rises when investors expect more market volatility.",
        calculation: "VIX is calculated using a weighted average of implied volatilities from S&P 500 index options with various strike prices and expiration dates.",
        interpretation: (value) => {
            if (value < 12) return "Very low VIX indicates extreme market complacency and low expected volatility. This may signal overconfidence.";
            if (value < 15) return "Low VIX suggests market complacency and low expected volatility. Markets are relatively calm.";
            if (value < 20) return "Moderate VIX indicates normal market conditions with standard volatility expectations.";
            if (value < 25) return "Elevated VIX suggests increased market fear and uncertainty. Investors are expecting more volatility.";
            if (value < 30) return "High VIX indicates significant market fear. Volatility expectations are elevated.";
            return "Very high VIX indicates extreme fear or panic in the markets. This often occurs during market crashes or major uncertainty.";
        },
        relevance: "Your algorithm uses VIX levels to adjust portfolio exposure. Higher VIX typically leads to reduced exposure for risk management, while lower VIX may allow for increased exposure when conditions are favorable.",
        historical: "During the 2008 financial crisis, VIX reached 80+. During normal bull markets, VIX typically stays below 20."
    },

    beta: {
        what: "**Beta** measures a portfolio's sensitivity to market movements. It compares how much your portfolio moves relative to the S&P 500 (which has a beta of 1.0).",
        calculation: "Beta is calculated using linear regression: Beta = Covariance(Portfolio Returns, Market Returns) / Variance(Market Returns). This is shown as the slope of the regression line in your correlation chart.",
        interpretation: (value) => {
            if (value < 0) return "Negative beta means the portfolio moves opposite to the market. This is very rare and indicates strong hedging or inverse correlation.";
            if (value < 0.5) return "Low beta indicates the portfolio is less sensitive to market movements. It will experience smaller swings than the market.";
            if (value < 1.0) return "Beta below 1.0 means the portfolio is less volatile than the market. It will rise and fall less than the S&P 500.";
            if (value === 1.0) return "Beta of 1.0 means the portfolio moves in line with the market. A 1% move in the S&P 500 typically results in a 1% move in your portfolio.";
            if (value < 1.5) return "Beta above 1.0 means the portfolio is more sensitive than the market. It will experience larger swings than the S&P 500.";
            return "High beta (above 1.5) indicates the portfolio is significantly more volatile than the market. It will amplify both gains and losses relative to the S&P 500.";
        },
        relevance: "Beta helps understand how your portfolio will react to market movements. A beta of 0.8 means if the market drops 10%, your portfolio might drop about 8%. Beta is influenced by leverage, asset allocation, and correlation with the market."
    },

    volatility: {
        what: "**Volatility** measures how much returns fluctuate over time. It quantifies the degree of variation in asset prices, indicating uncertainty and risk.",
        calculation: "Annualized volatility is calculated as: Standard Deviation of Daily Returns × √252 (trading days per year). Higher volatility means larger price swings in both directions.",
        interpretation: (value) => {
            if (value < 10) return "Low volatility indicates stable, predictable returns. The portfolio experiences minimal price fluctuations.";
            if (value < 15) return "Moderate volatility suggests normal market fluctuations. Returns are relatively stable with occasional swings.";
            if (value < 20) return "Elevated volatility indicates more significant price swings. The portfolio experiences noticeable fluctuations.";
            if (value < 30) return "High volatility suggests significant price swings and higher risk. Large gains and losses are more common.";
            return "Very high volatility indicates extreme market uncertainty and risk. The portfolio experiences dramatic price swings.";
        },
        relevance: "Your portfolio's volatility is compared to SPY's volatility. Lower volatility than SPY suggests more stable returns, while higher volatility indicates greater risk but potentially higher returns. The algorithm uses volatility to adjust exposure and manage risk.",
        relationship: "Volatility is directly related to risk. Higher volatility means higher risk but also potential for higher returns. The Sharpe ratio helps assess whether higher returns justify the increased volatility."
    },

    sharpe: {
        what: "The **Sharpe Ratio** measures risk-adjusted returns. It tells you how much excess return you're getting per unit of risk (volatility).",
        calculation: "Sharpe Ratio = (Portfolio Return - Risk-Free Rate) / Portfolio Volatility. Higher is better - it means you're getting more return for the risk taken.",
        interpretation: (value) => {
            if (value < 0) return "Negative Sharpe ratio means the portfolio underperformed the risk-free rate. Returns don't justify the risk.";
            if (value < 1) return "Sharpe ratio below 1.0 is considered poor. Returns barely compensate for the risk taken.";
            if (value < 2) return "Sharpe ratio between 1-2 is considered good. Returns adequately compensate for the risk.";
            if (value < 3) return "Sharpe ratio between 2-3 is considered very good. Excellent risk-adjusted returns.";
            return "Sharpe ratio above 3.0 is considered excellent. Outstanding risk-adjusted returns.";
        },
        relevance: "The Sharpe ratio helps evaluate whether your portfolio's returns justify the volatility. A portfolio with high returns but also high volatility might have a lower Sharpe ratio than a portfolio with moderate returns but low volatility."
    },

    correlation: {
        what: "**Correlation** measures how closely two assets move together. It ranges from -1 (perfect inverse) to +1 (perfect positive).",
        interpretation: (value) => {
            if (value < -0.5) return "Strong negative correlation means the assets move in opposite directions. When one rises, the other typically falls.";
            if (value < 0) return "Negative correlation means the assets tend to move in opposite directions.";
            if (value < 0.3) return "Low positive correlation means the assets move somewhat independently.";
            if (value < 0.7) return "Moderate correlation means the assets move together to some extent.";
            if (value < 0.9) return "High correlation means the assets move together closely.";
            return "Very high correlation means the assets move almost identically. Little diversification benefit.";
        },
        relevance: "Correlation between your portfolio and SPY shows how closely they move together. High correlation (close to 1.0) means your portfolio behaves similarly to the market. Lower correlation provides diversification benefits."
    },

    spy: {
        what: "**SPY (SPDR S&P 500 ETF Trust)** is an exchange-traded fund that tracks the S&P 500 index, representing the 500 largest U.S. companies by market capitalization.",
        characteristics: "SPY is one of the most liquid and widely traded ETFs, with low expense ratios. It's commonly used as a market benchmark because it represents broad U.S. equity market performance.",
        relevance: "Your portfolio uses SPY as the core equity exposure component. It provides broad market exposure with lower volatility than leveraged ETFs. The algorithm adjusts SPY exposure based on market conditions and risk profile.",
        performance: "SPY has historically returned approximately 10% annually over long periods, though with significant year-to-year volatility. It's considered a good proxy for overall U.S. stock market performance."
    },

    tqqq: {
        what: "**TQQQ (ProShares UltraPro QQQ)** is a leveraged ETF that aims to provide 3x the daily returns of the NASDAQ-100 index.",
        mechanics: "TQQQ uses financial derivatives to achieve 3x leverage. If QQQ (the underlying NASDAQ-100 ETF) moves 1% in a day, TQQQ typically moves approximately 3% in the same direction.",
        risks: "Leveraged ETFs amplify both gains and losses. They're designed for daily returns, not long-term holding. Over multiple days, returns can diverge from exactly 3x due to compounding effects and volatility decay.",
        relevance: "Your algorithm uses TQQQ strategically during favorable market conditions identified by the algorithm. It increases exposure when market conditions suggest higher return potential, but reduces or eliminates it during unfavorable conditions to protect capital.",
        warning: "Leveraged ETFs like TQQQ are high-risk investments. They can experience significant losses during market downturns and may not be suitable for all investors. The algorithm manages this risk through dynamic allocation."
    },

    upro: {
        what: "**UPRO (ProShares UltraPro S&P500)** is a leveraged ETF that aims to provide 3x the daily returns of the S&P 500 index.",
        mechanics: "UPRO uses financial derivatives to achieve 3x leverage. If SPY (the underlying S&P 500 ETF) moves 1% in a day, UPRO typically moves approximately 3% in the same direction.",
        risks: "Leveraged ETFs amplify both gains and losses. They're designed for daily returns, not long-term holding. Over multiple days, returns can diverge from exactly 3x due to compounding effects and volatility decay.",
        relevance: "Your algorithm uses UPRO strategically during favorable market conditions identified by the algorithm. It increases exposure when market conditions suggest higher return potential, but reduces or eliminates it during unfavorable conditions to protect capital.",
        difference: "UPRO tracks the S&P 500 (broad market), while TQQQ tracks the NASDAQ-100 (tech-heavy). UPRO provides broader market exposure, while TQQQ focuses on technology stocks.",
        warning: "Leveraged ETFs like UPRO are high-risk investments. They can experience significant losses during market downturns and may not be suitable for all investors. The algorithm manages this risk through dynamic allocation."
    },

    treasuryBills: {
        what: "**Treasury Bills (T-bills)** are short-term debt securities issued by the U.S. government with maturities of one year or less. They're considered one of the safest investments because they're backed by the full faith and credit of the U.S. government.",
        characteristics: "T-bills are sold at a discount and mature at face value. They don't pay periodic interest like bonds, but instead provide returns through the difference between purchase price and face value.",
        types: "Treasury securities come in different maturities:\n- **T-bills**: 4 weeks to 1 year\n- **T-notes**: 2 to 10 years\n- **T-bonds**: 20 to 30 years",
        safety: "Treasury securities are considered 'risk-free' because the U.S. government has never defaulted on its debt. They're used as a benchmark for risk-free rate in financial calculations.",
        returns: "T-bills typically offer lower returns than stocks or bonds because of their safety. Current yields vary with market conditions and Federal Reserve policy. They're ideal for capital preservation.",
        relevance: "Your portfolio uses T-bills (or cash) as the risk-free asset component. When market conditions are unfavorable or volatility is high, the algorithm increases allocation to T-bills to protect capital. This provides stability and liquidity while waiting for better investment opportunities.",
        role: "In your portfolio, T-bills serve as a safe haven during market uncertainty. Higher T-bill allocation means the algorithm is being more conservative, preserving capital for when market conditions improve."
    },

    qqq: {
        what: "**QQQ (Invesco QQQ Trust)** tracks the NASDAQ-100 index, which includes the 100 largest non-financial companies listed on NASDAQ.",
        characteristics: "QQQ is heavily weighted toward technology stocks (Apple, Microsoft, Amazon, etc.) and is more volatile than SPY. It represents growth-oriented companies.",
        difference: "QQQ focuses on large-cap tech and growth stocks, while SPY represents the broader U.S. market. QQQ tends to be more volatile and can outperform SPY during tech bull markets."
    },

    leverage: {
        what: "**Leverage** refers to using borrowed money or financial instruments (like leveraged ETFs) to amplify investment returns.",
        how: "Leverage magnifies both gains and losses. If you have 3x leverage and the market moves 10%, your position moves approximately 30%.",
        risks: "Leverage increases risk significantly. A 10% market decline with 3x leverage results in approximately a 30% loss. Leverage can also lead to margin calls or forced liquidation in extreme scenarios.",
        relevance: "Your portfolio uses leverage through TQQQ. The algorithm dynamically adjusts leverage based on market conditions - increasing it during favorable conditions and reducing it during unfavorable conditions to manage risk."
    },

    returns: {
        what: "**Returns** measure the percentage gain or loss on an investment over a specific period.",
        calculation: "Returns can be calculated as:\n- **Simple Return**: (End Price - Start Price) / Start Price\n- **Logarithmic Return**: ln(End Price / Start Price)\n- **Cumulative Return**: Sum of all period returns over time",
        types: "Returns can be measured over different periods (daily, monthly, annual) and can be absolute (total return) or relative (compared to a benchmark like SPY).",
        relevance: "Your dashboard shows cumulative returns over different timeframes (3 months, 1 year, 3 years) to evaluate long-term performance. Returns are compared to SPY to assess relative performance.",
        compounding: "Returns compound over time. A 10% return followed by another 10% return results in a 21% total return (1.10 × 1.10 = 1.21), not 20%."
    },

    momentum: {
        what: "**Momentum** is a market indicator that measures the strength and direction of price trends. It helps identify whether a trend is likely to continue.",
        interpretation: "Momentum is calculated using price rank over rolling windows. Higher momentum suggests stronger upward trends, while lower momentum may indicate weakening trends or potential reversals.",
        relevance: "Your algorithm uses momentum as one of several factors to determine exposure. Higher momentum may allow for increased exposure, while low momentum may trigger reduced exposure for risk management."
    },

    riskAversion: {
        what: "**Risk Aversion** measures market participants' willingness to take risk. It's derived from correlations between volatility dynamics and market movements.",
        interpretation: "Higher risk aversion indicates investors are more cautious and prefer safer assets. Lower risk aversion suggests investors are more willing to take risks.",
        relevance: "Your algorithm incorporates risk aversion to adjust portfolio exposure. During periods of high risk aversion, the algorithm may reduce exposure to protect capital. During low risk aversion periods, it may increase exposure to capture opportunities."
    },

    marketDirection: {
        what: "**Market Direction** is an indicator that measures the overall trend of the market over a rolling time window, expressed as an annualized return.",
        interpretation: "Positive market direction suggests an upward trend, while negative suggests a downward trend. The magnitude indicates the strength of the trend.",
        relevance: "Your algorithm uses market direction to assess overall market conditions. Positive market direction may support increased exposure, while negative direction may trigger reduced exposure."
    },

    exposure: {
        what: "**Exposure** represents the total percentage of your portfolio allocated to equity markets. It's the sum of SPY-correlated exposure plus leveraged ETF exposure.",
        calculation: "Full Exposure = SPY Exposure + Leveraged ETF Exposure. Exposure ranges from 0% (fully in cash) to over 100% (using leverage).",
        relevance: "Your dashboard shows how exposure changes over time. Higher exposure means more invested in equities, which offers higher return potential but also higher risk. The algorithm dynamically adjusts exposure based on market conditions.",
        interpretation: "Exposure above 100% means leverage is being used. Exposure below 50% means the portfolio is more conservative, with significant cash holdings."
    },

    allocation: {
        what: "**Portfolio Allocation** refers to how your portfolio is divided among different asset classes: SPY-correlated ETFs, leveraged ETFs (TQQQ), and risk-free assets (cash/Treasury bills).",
        relevance: "Your allocation is dynamically adjusted by the algorithm based on market conditions, volatility, momentum, and your selected risk profile. The allocation adapts to optimize risk-adjusted returns.",
        components: "The three main components are:\n- **SPY Exposure**: Broad market equity exposure with lower volatility\n- **TQQQ Exposure**: Leveraged growth exposure for higher return potential\n- **Cash**: Risk-free assets providing stability and liquidity"
    },

    riskProfile: {
        what: "**Risk Profile** determines how conservative or aggressive the algorithm's strategy is. Three profiles are available: Averse, Neutral, and Seeker.",
        averse: "**Averse** profile focuses on capital preservation with lower volatility. It takes more conservative positions and reduces exposure during uncertain conditions.",
        neutral: "**Neutral** profile balances growth and preservation. It takes measured positions aiming for consistent, moderate returns.",
        seeker: "**Seeker** profile maximizes growth potential with higher leverage and more aggressive positions. It takes more risk for higher return potential.",
        relevance: "Your selected risk profile affects all portfolio decisions - allocation, exposure levels, leverage usage, and risk management. The algorithm adapts its strategy based on your risk tolerance."
    },

    market: {
        what: "The **stock market** (or equity market) is where shares of publicly traded companies are bought and sold. The U.S. stock market includes major exchanges like NYSE and NASDAQ.",
        hours: "The U.S. stock market is open Monday-Friday from 9:30 AM to 4:00 PM Eastern Time, excluding holidays.",
        factors: "Markets are influenced by:\n- Economic data (GDP, inflation, employment)\n- Company earnings and fundamentals\n- Geopolitical events\n- Investor sentiment\n- Central bank policies\n- Market volatility and fear indicators (VIX)",
        relevance: "Your algorithm monitors market conditions continuously and adjusts portfolio exposure based on market status, volatility, trends, and other indicators. The dashboard shows whether the market is currently open or closed."
    },

    volatilityDecay: {
        what: "**Volatility Decay** (also called volatility drag) refers to how leveraged ETFs can underperform their target multiple over time due to daily rebalancing and compounding effects.",
        mechanics: "If a 3x leveraged ETF experiences +10% one day and -10% the next, it returns -1% (not 0%), while the underlying would return 0%. This decay is more pronounced during volatile periods.",
        relevance: "Your algorithm is aware of volatility decay and uses leveraged ETFs strategically, typically during favorable conditions and reducing exposure during volatile periods to minimize decay effects."
    }
};

/**
 * Get explanation for a financial concept
 * @param {string} concept - The concept name (e.g., 'vix', 'beta', 'volatility')
 * @param {*} currentValue - Current value from dashboard (optional)
 * @returns {object} Explanation object with what, interpretation, relevance, etc.
 */
export function getFinancialExplanation(concept, currentValue = null) {
    // Use translation system for getting knowledge
    const knowledge = getTranslatedKnowledge(concept.toLowerCase());
    if (!knowledge) return null;

    const result = {
        what: knowledge.what || '',
        calculation: knowledge.calculation || null,
        relevance: knowledge.relevance || null,
        historical: knowledge.historical || null,
        interpretation: null
    };

    // If we have a current value, get interpretation from translation system
    if (currentValue !== null) {
        result.interpretation = getInterpretation(concept.toLowerCase(), currentValue);
        result.currentValue = currentValue;
    }

    return result;
}

/**
 * Normalize text for better matching
 * Removes hyphens, handles spaces, normalizes common variations
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
function normalizeForMatching(text) {
    return text
        .toLowerCase()
        .replace(/[-_]/g, '')           // Remove hyphens and underscores
        .replace(/\s+/g, ' ')            // Normalize multiple spaces
        .replace(/[^\w\s]/g, '')         // Remove punctuation (but keep spaces for word boundaries)
        .trim();
}

/**
 * Check if a question is asking about a financial concept
 * Enhanced with fuzzy matching, normalization, and bilingual support
 * @param {string} question - User's question
 * @returns {string|null} - Concept name if found, null otherwise
 */
export function detectFinancialConcept(question) {
    const q = question.toLowerCase().trim();
    const normalizedQ = normalizeForMatching(q);
    
    // Enhanced concept map with comprehensive variations (English + Spanish)
    const conceptMap = {
        'vix': [
            // English
            'vix', 'volatility index', 'fear gauge', 'volatility gauge', 'fear index', 'cboe volatility',
            // Spanish
            'índice de volatilidad', 'indicador del miedo', 'gauge del miedo', 'vix'
        ],
        'beta': [
            // English
            'beta', 'market sensitivity', 'correlation with market', 'market beta', 'portfolio beta', 'beta coefficient',
            // Spanish
            'beta', 'sensibilidad del mercado', 'correlación con el mercado', 'beta del mercado', 'beta del portafolio'
        ],
        'volatility': [
            // English
            'volatility', 'vol', 'price swings', 'fluctuation', 'price variation', 'volatile', 'volatilty', 'volatiliy',
            // Spanish
            'volatilidad', 'vol', 'oscilaciones de precio', 'fluctuación', 'variación de precio', 'volátil'
        ],
        'sharpe': [
            // English
            'sharpe', 'sharpe ratio', 'risk-adjusted return', 'risk-adjusted',
            // Spanish
            'sharpe', 'ratio de sharpe', 'ratio sharpe', 'retorno ajustado por riesgo', 'ajustado por riesgo'
        ],
        'correlation': [
            // English
            'correlation', 'correlated', 'moves together', 'correlate', 'correlates',
            // Spanish
            'correlación', 'correlacionado', 'se mueven juntos', 'correlaciona', 'correlacionan'
        ],
        'spy': [
            // English
            'spy', 's&p 500', 'sp500', 'spdr', 's&p', 'sp500 etf', 'spy etf',
            // Spanish
            'spy', 's&p 500', 'sp500', 'spdr', 's&p', 'etf spy'
        ],
        'tqqq': [
            // English
            'tqqq', '3x qqq', 'leveraged qqq', 'ultrapro qqq', 'triple qqq', 'tqqq etf',
            // Spanish
            'tqqq', 'qqq 3x', 'qqq apalancado', 'ultrapro qqq', 'triple qqq', 'etf tqqq'
        ],
        'upro': [
            // English
            'upro', '3x spy', 'leveraged spy', 'ultrapro s&p', 'ultrapro sp500', 's&p 500 3x', 'ultrapro', 'triple spy', 'upro etf',
            // Spanish
            'upro', 'spy 3x', 'spy apalancado', 'ultrapro s&p', 'ultrapro sp500', 's&p 500 3x', 'ultrapro', 'triple spy', 'etf upro'
        ],
        'treasuryBills': [
            // English - Standard forms
            't-bill', 't-bills', 't-note', 't-notes', 't-bond', 't-bonds',
            // English - Without hyphens (common user input)
            'tbill', 'tbills', 't bill', 't bills', 't note', 't notes', 'tnote', 'tnotes', 't bond', 't bonds', 'tbond', 'tbonds',
            // English - Full names
            'treasury bill', 'treasury bills', 'treasury bond', 'treasury bonds', 'treasury note', 'treasury notes',
            'treasury security', 'treasury securities',
            // English - Related terms
            'us treasury', 'government bond', 'government bonds', 'govt bond', 'risk-free', 'risk free asset',
            'safe asset', 'safe investment', 'cash allocation', 'why cash', 'cash equivalent', 'treasury',
            // Spanish - Standard forms
            'letra del tesoro', 'letras del tesoro', 't-bill', 't-bills', 'bono del tesoro', 'bonos del tesoro',
            'nota del tesoro', 'notas del tesoro',
            // Spanish - Without hyphens
            'tbill', 'tbills', 't bill', 't bills', 'letra tesoro', 'letras tesoro',
            // Spanish - Related terms
            'tesoro de ee.uu.', 'tesoro de ee uu', 'tesoro estadounidense', 'bono gubernamental', 'bonos gubernamentales',
            'activo libre de riesgo', 'activo sin riesgo', 'inversión segura', 'asignación de efectivo',
            'por qué efectivo', 'equivalente de efectivo', 'tesoro'
        ],
        'qqq': [
            // English
            'qqq', 'nasdaq 100', 'nasdaq', 'qqq etf', 'nasdaq etf', 'nasdaq100',
            // Spanish
            'qqq', 'nasdaq 100', 'nasdaq', 'etf qqq', 'etf nasdaq', 'nasdaq100'
        ],
        'leverage': [
            // English
            'leverage', 'leveraged', 'leverage ratio', 'margin', 'leveraging',
            // Spanish
            'apalancamiento', 'apalancado', 'ratio de apalancamiento', 'margen', 'apalancar'
        ],
        'returns': [
            // English
            'return', 'returns', 'performance', 'yield', 'gain', 'loss', 'annual return', 'roi', 'return on investment',
            // Spanish
            'retorno', 'retornos', 'rendimiento', 'rendimientos', 'rendimiento anual', 'ganancia', 'pérdida', 'roi'
        ],
        'momentum': [
            // English
            'momentum', 'trend', 'price trend', 'market trend', 'momentum indicator', 'trending',
            // Spanish
            'momentum', 'momento', 'tendencia', 'tendencia de precio', 'tendencia del mercado', 'indicador de momentum'
        ],
        'riskAversion': [
            // English
            'risk aversion', 'risk averse', 'market fear', 'risk tolerance',
            // Spanish
            'aversión al riesgo', 'aversión riesgo', 'miedo del mercado', 'tolerancia al riesgo'
        ],
        'marketDirection': [
            // English
            'market direction', 'market trend', 'market momentum', 'direction', 'market movement',
            // Spanish
            'dirección del mercado', 'tendencia del mercado', 'momentum del mercado', 'dirección', 'movimiento del mercado'
        ],
        'exposure': [
            // English
            'exposure', 'equity exposure', 'market exposure', 'portfolio exposure', 'exposed',
            // Spanish
            'exposición', 'exposición a renta variable', 'exposición al mercado', 'exposición del portafolio', 'expuesto'
        ],
        'allocation': [
            // English
            'allocation', 'portfolio allocation', 'asset allocation', 'allocated', 'allocate',
            // Spanish
            'asignación', 'asignación del portafolio', 'asignación de activos', 'asignado', 'asignar'
        ],
        'riskProfile': [
            // English
            'risk profile', 'risk tolerance', 'risk preference', 'risk level', 'risk setting',
            // Spanish
            'perfil de riesgo', 'tolerancia al riesgo', 'preferencia de riesgo', 'nivel de riesgo', 'configuración de riesgo'
        ],
        'market': [
            // English
            'market', 'stock market', 'equity market', 'financial market', 'markets', 'stock exchange',
            // Spanish
            'mercado', 'mercado de acciones', 'mercado de renta variable', 'mercado financiero', 'mercados', 'bolsa de valores'
        ],
        'volatilityDecay': [
            // English
            'volatility decay', 'volatility drag', 'leveraged decay', 'decay', 'drag',
            // Spanish
            'decaimiento por volatilidad', 'arrastre por volatilidad', 'decaimiento apalancado', 'decaimiento', 'arrastre'
        ]
    };

    // Strategy 1: Exact matching (fastest, most common case)
    for (const [concept, keywords] of Object.entries(conceptMap)) {
        if (keywords.some(keyword => q.includes(keyword))) {
            return concept;
        }
    }
    
    // Strategy 2: Normalized matching (catches "tbill" vs "t-bill", "t bill" vs "t-bill")
    for (const [concept, keywords] of Object.entries(conceptMap)) {
        const normalizedKeywords = keywords.map(k => normalizeForMatching(k));
        if (normalizedKeywords.some(keyword => normalizedQ.includes(keyword))) {
            return concept;
        }
    }
    
    // Strategy 3: Word boundary matching (most accurate, prevents false positives)
    // This catches cases like "what is tbill?" where "tbill" is a separate word
    for (const [concept, keywords] of Object.entries(conceptMap)) {
        for (const keyword of keywords) {
            const normalizedKeyword = normalizeForMatching(keyword);
            // Escape special regex characters
            const escapedKeyword = normalizedKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Use word boundary for better matching
            const wordBoundaryRegex = new RegExp(`\\b${escapedKeyword}\\b`, 'i');
            if (wordBoundaryRegex.test(normalizedQ)) {
                return concept;
            }
        }
    }

    return null;
}

